import { workspaceApiFetch } from '../../cloud';
export {
  type DatabaseSystemDocAccessBinding,
  postDatabaseSystemDocAccessBindings,
} from '../../doc/services/database-system-doc-access';

export type DatabaseImportColumnPayload = {
  id: string;
  name: string;
  type: string;
  data?: Record<string, unknown>;
};

export type DatabaseImportRowPayload = {
  id: string;
  values: Record<string, unknown>;
};

export type DatabaseImportSourcePayload = {
  clientId: string;
  title: string;
  x: number;
  y: number;
  columns: DatabaseImportColumnPayload[];
  titleColumnId: string;
  rows: DatabaseImportRowPayload[];
  sourceFileName?: string;
  sourceSheetName?: string;
};

export type DatabaseImportJob = {
  id: string;
  status: string;
  totalRows: number;
  processedRows: number;
  shardCount: number;
  totalBytes: number;
  errorMessage?: string | null;
};

export type DatabaseImportJobStatusChange = (jobs: DatabaseImportJob[]) => void;

export type DatabaseImportBatchResult = {
  jobs: DatabaseImportJob[];
};

function databaseImportBaseUrl(workspaceId: string, docId?: string): string {
  const workspacePath = `/api/workspace/${encodeURIComponent(workspaceId)}/database-imports`;
  if (!docId) return workspacePath;
  return `${workspacePath}/docs/${encodeURIComponent(docId)}`;
}

async function readError(
  response: Response,
  fallback: string
): Promise<string> {
  const message = await response.text();
  return message || fallback;
}

export async function postDatabaseImportBatch(input: {
  workspaceId: string;
  docId: string;
  sources: DatabaseImportSourcePayload[];
}): Promise<DatabaseImportBatchResult> {
  const response = await workspaceApiFetch(
    `${databaseImportBaseUrl(input.workspaceId, input.docId)}/import-batch`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ databases: input.sources }),
    },
    foregroundPolicy(input.workspaceId, 'database-import-batch')
  );

  if (!response.ok) {
    throw new Error(
      await readError(response, `Database import failed (${response.status})`)
    );
  }

  return response.json() as Promise<DatabaseImportBatchResult>;
}

async function fetchImportJob(
  workspaceId: string,
  jobId: string
): Promise<DatabaseImportJob> {
  const response = await workspaceApiFetch(
    `${databaseImportBaseUrl(workspaceId)}/jobs/${encodeURIComponent(jobId)}`,
    { credentials: 'include' },
    {
      workspaceId,
      category: 'workspace-api',
      key: `database-import-job:${jobId}`,
      mode: 'background',
      priority: 'background',
    }
  );
  if (!response.ok) {
    throw new Error(
      await readError(
        response,
        `Database import status failed (${response.status})`
      )
    );
  }
  return response.json() as Promise<DatabaseImportJob>;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isTerminalJob(job: DatabaseImportJob): boolean {
  return (
    job.status === 'completed' ||
    job.status === 'failed' ||
    job.status === 'cancelled'
  );
}

function assertJobsCompleted(jobs: DatabaseImportJob[]): void {
  const failed = jobs.find(job => job.status === 'failed');
  if (failed) {
    throw new Error(
      failed.errorMessage || `Database import job ${failed.id} failed`
    );
  }
  const cancelled = jobs.find(job => job.status === 'cancelled');
  if (cancelled) {
    throw new Error(`Database import job ${cancelled.id} was cancelled`);
  }
}

export async function waitForDatabaseImportJobs(
  workspaceId: string,
  jobs: DatabaseImportJob[],
  onJobsChanged?: DatabaseImportJobStatusChange
): Promise<DatabaseImportJob[]> {
  let current = jobs;
  onJobsChanged?.(current);

  while (!current.every(isTerminalJob)) {
    await sleep(1000);
    current = await Promise.all(
      current.map(job => fetchImportJob(workspaceId, job.id))
    );
    onJobsChanged?.(current);
  }

  assertJobsCompleted(current);
  return current;
}

async function postJobAction(
  workspaceId: string,
  jobId: string,
  action: 'cancel' | 'retry'
): Promise<DatabaseImportJob> {
  const response = await workspaceApiFetch(
    `${databaseImportBaseUrl(workspaceId)}/jobs/${encodeURIComponent(jobId)}/${action}`,
    {
      method: 'POST',
      credentials: 'include',
    },
    foregroundPolicy(workspaceId, `database-import-${action}:${jobId}`)
  );
  if (!response.ok) {
    throw new Error(
      await readError(
        response,
        `Database import ${action} failed (${response.status})`
      )
    );
  }
  return response.json() as Promise<DatabaseImportJob>;
}

export function cancelDatabaseImportJob(
  workspaceId: string,
  jobId: string
): Promise<DatabaseImportJob> {
  return postJobAction(workspaceId, jobId, 'cancel');
}

export function retryDatabaseImportJob(
  workspaceId: string,
  jobId: string
): Promise<DatabaseImportJob> {
  return postJobAction(workspaceId, jobId, 'retry');
}

function foregroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'foreground-action' as const,
    key,
    mode: 'foreground' as const,
    priority: 'foreground' as const,
  };
}
